import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { reportsAPI, projectsAPI, usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Download, Filter, Star, Calendar, RefreshCw, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = '/api';

// Health Score Badge Component
const HealthScoreBadge = ({ score, status, size = 'md' }) => {
  if (!score && score !== 0) {
    return (
      <div className="flex items-center gap-1 text-xs text-slate-400">
        <Star className="w-3 h-3" />
        <span>-</span>
      </div>
    );
  }

  const statusColors = {
    excellent: 'bg-green-100 text-green-700',
    healthy: 'bg-blue-100 text-blue-700',
    at_risk: 'bg-amber-100 text-amber-700',
    critical: 'bg-red-100 text-red-700',
    no_data: 'bg-slate-100 text-slate-600'
  };

  const stars = [];
  const fullStars = Math.floor(score);
  const hasHalf = score - fullStars >= 0.5;
  const starSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  
  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(<Star key={i} className={`${starSize} fill-amber-400 text-amber-400`} />);
    } else if (i === fullStars && hasHalf) {
      stars.push(<Star key={i} className={`${starSize} fill-amber-400/50 text-amber-400`} />);
    } else {
      stars.push(<Star key={i} className={`${starSize} text-slate-300`} />);
    }
  }

  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${statusColors[status] || statusColors.no_data}`}>
      <div className="flex items-center gap-0.5">{stars}</div>
      <span className="font-semibold text-sm">{score.toFixed(1)}</span>
    </div>
  );
};

const TrendIndicator = ({ value }) => {
  if (value === null || value === undefined) return null;
  
  if (value > 0) {
    return (
      <span className="flex items-center text-green-600 text-xs">
        <TrendingUp className="w-3 h-3 mr-0.5" />
        +{value.toFixed(2)}
      </span>
    );
  } else if (value < 0) {
    return (
      <span className="flex items-center text-red-600 text-xs">
        <TrendingDown className="w-3 h-3 mr-0.5" />
        {value.toFixed(2)}
      </span>
    );
  }
  return (
    <span className="flex items-center text-slate-500 text-xs">
      <Minus className="w-3 h-3 mr-0.5" />
      0
    </span>
  );
};

export const Reports = () => {
  const { user } = useAuth();
  const [timeByProject, setTimeByProject] = useState([]);
  const [timeByUser, setTimeByUser] = useState([]);
  const [stats, setStats] = useState(null);
  const [healthData, setHealthData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    selectedMonth: new Date().toISOString().slice(0, 7),
    projectId: 'all',
    userId: 'all',
    healthStatus: 'all'
  });
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadFilteredData();
  }, [filters.startDate, filters.endDate, filters.projectId, filters.userId]);

  useEffect(() => {
    loadHealthData();
  }, [filters.selectedMonth, filters.healthStatus]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [projectsRes, usersRes] = await Promise.all([
        projectsAPI.getAll(),
        usersAPI.getAll()
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
      await loadFilteredData();
      await loadHealthData();
    } catch (error) {
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const loadFilteredData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.projectId && filters.projectId !== 'all') params.append('project_id', filters.projectId);
      if (filters.userId && filters.userId !== 'all') params.append('user_id', filters.userId);

      const [statsRes, projectRes] = await Promise.all([
        reportsAPI.getDashboard(),
        reportsAPI.getTimeByProject()
      ]);
      setStats(statsRes.data);
      setTimeByProject(projectRes.data);

      if (user?.role !== 'team_member') {
        const userRes = await reportsAPI.getTimeByUser();
        setTimeByUser(userRes.data);
      }
    } catch (error) {
      console.error('Failed to load filtered data');
    }
  };

  const loadHealthData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/all-projects-health?month=${filters.selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let data = response.data.projects || [];
      
      // Filter by health status
      if (filters.healthStatus !== 'all') {
        data = data.filter(p => p.health_status === filters.healthStatus);
      }
      
      setHealthData(data);
    } catch (error) {
      console.error('Failed to load health data');
    }
  };

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      selectedMonth: new Date().toISOString().slice(0, 7),
      projectId: 'all',
      userId: 'all',
      healthStatus: 'all'
    });
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(20);
    doc.text('Adsem Performance - Raport Complet', 14, 20);
    
    doc.setFontSize(12);
    doc.text(`Generat: ${new Date().toLocaleString('ro-RO')}`, 14, 30);
    doc.text(`Perioada: ${filters.selectedMonth}`, 14, 38);
    
    // Summary
    doc.text('Sumar', 14, 50);
    doc.autoTable({
      startY: 55,
      head: [['Metrică', 'Valoare']],
      body: [
        ['Total Proiecte', stats?.projects_count || 0],
        ['Total Taskuri', stats?.tasks_count || 0],
        ['Total Ore', `${stats?.total_hours || 0}h`],
      ],
    });

    // Health Scores
    const healthTableY = doc.lastAutoTable.finalY + 10;
    doc.text('Health Score pe Proiecte', 14, healthTableY);
    doc.autoTable({
      startY: healthTableY + 5,
      head: [['Proiect', 'Health Score', 'Delivery', 'Happiness', 'NPS', 'Status']],
      body: healthData.map(p => [
        p.project_name,
        p.health_score?.toFixed(2) || '-',
        p.delivery_score?.toFixed(1) || '-',
        p.happiness_score?.toFixed(1) || '-',
        p.nps_score ?? '-',
        p.health_status
      ]),
    });

    // Time by Project
    const projectTableY = doc.lastAutoTable.finalY + 10;
    doc.text('Timp pe Proiecte', 14, projectTableY);
    doc.autoTable({
      startY: projectTableY + 5,
      head: [['Proiect', 'Ore Totale', 'Înregistrări']],
      body: timeByProject.map(p => [p.project_name, `${p.total_hours}h`, p.entries_count]),
    });

    if (timeByUser.length > 0) {
      const userTableY = doc.lastAutoTable.finalY + 10;
      doc.text('Timp pe Utilizatori', 14, userTableY);
      doc.autoTable({
        startY: userTableY + 5,
        head: [['Utilizator', 'Email', 'Ore Totale', 'Înregistrări']],
        body: timeByUser.map(u => [u.user_name, u.user_email, `${u.total_hours}h`, u.entries_count]),
      });
    }

    doc.save(`raport-${filters.selectedMonth}.pdf`);
    toast.success('PDF exportat cu succes');
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Summary
    const summaryData = [
      ['Metrică', 'Valoare'],
      ['Total Proiecte', stats?.projects_count || 0],
      ['Total Taskuri', stats?.tasks_count || 0],
      ['Total Ore', stats?.total_hours || 0],
      ['Perioada', filters.selectedMonth],
    ];
    const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Sumar');

    // Health Scores
    const healthSheetData = [
      ['Proiect', 'Health Score', 'Delivery Score', 'Happiness Score', 'NPS', 'Status', 'Health Trend'],
      ...healthData.map(p => [
        p.project_name,
        p.health_score?.toFixed(2) || '',
        p.delivery_score?.toFixed(1) || '',
        p.happiness_score?.toFixed(1) || '',
        p.nps_score ?? '',
        p.health_status,
        p.health_trend?.toFixed(2) || ''
      ])
    ];
    const healthWs = XLSX.utils.aoa_to_sheet(healthSheetData);
    XLSX.utils.book_append_sheet(wb, healthWs, 'Health Score');

    // Time by Project
    const projectData = [
      ['Proiect', 'Ore Totale', 'Înregistrări'],
      ...timeByProject.map(p => [p.project_name, p.total_hours, p.entries_count])
    ];
    const projectWs = XLSX.utils.aoa_to_sheet(projectData);
    XLSX.utils.book_append_sheet(wb, projectWs, 'Timp pe Proiecte');

    // Time by User
    if (timeByUser.length > 0) {
      const userData = [
        ['Utilizator', 'Email', 'Ore Totale', 'Înregistrări'],
        ...timeByUser.map(u => [u.user_name, u.user_email, u.total_hours, u.entries_count])
      ];
      const userWs = XLSX.utils.aoa_to_sheet(userData);
      XLSX.utils.book_append_sheet(wb, userWs, 'Timp pe Utilizatori');
    }

    XLSX.writeFile(wb, `raport-${filters.selectedMonth}.xlsx`);
    toast.success('Excel exportat cu succes');
  };

  const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1'];

  // Calculate health stats
  const healthStats = {
    excellent: healthData.filter(p => p.health_status === 'excellent').length,
    healthy: healthData.filter(p => p.health_status === 'healthy').length,
    at_risk: healthData.filter(p => p.health_status === 'at_risk').length,
    critical: healthData.filter(p => p.health_status === 'critical').length,
    no_data: healthData.filter(p => p.health_status === 'no_data').length,
  };

  const avgHealthScore = healthData.length > 0 
    ? healthData.reduce((sum, p) => sum + (p.health_score || 0), 0) / healthData.filter(p => p.health_score).length
    : 0;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-4xl font-bold tracking-tight" data-testid="reports-title">Reports & Analytics</h1>
            <p className="mt-2 text-muted-foreground">Rapoarte complete cu Health Score și filtre avansate</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => setShowFilters(!showFilters)} variant="outline">
              <Filter className="mr-2 h-4 w-4" />
              {showFilters ? 'Ascunde Filtre' : 'Arată Filtre'}
            </Button>
            <Button onClick={exportToPDF} variant="outline" data-testid="export-pdf-btn">
              <Download className="mr-2 h-4 w-4" />
              Export PDF
            </Button>
            <Button onClick={exportToExcel} className="bg-brand-orange hover:bg-brand-orange/90" data-testid="export-excel-btn">
              <Download className="mr-2 h-4 w-4" />
              Export Excel
            </Button>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filtre
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <Label className="text-xs">Lună Health Score</Label>
                  <Input
                    type="month"
                    value={filters.selectedMonth}
                    onChange={(e) => setFilters(f => ({ ...f, selectedMonth: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Data Început</Label>
                  <Input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters(f => ({ ...f, startDate: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Data Sfârșit</Label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters(f => ({ ...f, endDate: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Proiect</Label>
                  <Select value={filters.projectId} onValueChange={(v) => setFilters(f => ({ ...f, projectId: v }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Toate" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate Proiectele</SelectItem>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Health Status</Label>
                  <Select value={filters.healthStatus} onValueChange={(v) => setFilters(f => ({ ...f, healthStatus: v }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Toate" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate</SelectItem>
                      <SelectItem value="excellent">Excelent</SelectItem>
                      <SelectItem value="healthy">Sănătos</SelectItem>
                      <SelectItem value="at_risk">La Risc</SelectItem>
                      <SelectItem value="critical">Critic</SelectItem>
                      <SelectItem value="no_data">Fără Date</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={resetFilters} className="w-full">
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Resetează
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Health Score Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <p className="text-xs text-green-600 font-medium">EXCELENT</p>
              <p className="text-2xl font-bold text-green-700">{healthStats.excellent}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <p className="text-xs text-blue-600 font-medium">SĂNĂTOS</p>
              <p className="text-2xl font-bold text-blue-700">{healthStats.healthy}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <p className="text-xs text-amber-600 font-medium">LA RISC</p>
              <p className="text-2xl font-bold text-amber-700">{healthStats.at_risk}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <p className="text-xs text-red-600 font-medium">CRITIC</p>
              <p className="text-2xl font-bold text-red-700">{healthStats.critical}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 font-medium">FĂRĂ DATE</p>
              <p className="text-2xl font-bold text-slate-700">{healthStats.no_data}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4">
              <p className="text-xs text-purple-600 font-medium">MEDIA HEALTH</p>
              <p className="text-2xl font-bold text-purple-700">
                {avgHealthScore ? avgHealthScore.toFixed(2) : '-'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Health Score Table */}
        <Card data-testid="health-score-table">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Health Score pe Proiecte - {filters.selectedMonth}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/20 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Proiect</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Health Score</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Delivery</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Happiness</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">NPS</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Trend</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {healthData.map((project, idx) => (
                    <tr key={idx} className="border-b border-border hover:bg-muted/50">
                      <td className="px-4 py-3 text-sm font-medium">{project.project_name}</td>
                      <td className="px-4 py-3">
                        <HealthScoreBadge score={project.health_score} status={project.health_status} />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {project.delivery_score ? (
                          <span className="font-mono">{project.delivery_score.toFixed(1)}</span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {project.happiness_score ? (
                          <span className="font-mono">{project.happiness_score.toFixed(1)}</span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {project.nps_score !== null && project.nps_score !== undefined ? (
                          <span className={`font-mono ${project.nps_score >= 9 ? 'text-green-600' : project.nps_score >= 7 ? 'text-amber-600' : 'text-red-600'}`}>
                            {project.nps_score}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <TrendIndicator value={project.health_trend} />
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium uppercase ${
                          project.health_status === 'excellent' ? 'bg-green-100 text-green-700' :
                          project.health_status === 'healthy' ? 'bg-blue-100 text-blue-700' :
                          project.health_status === 'at_risk' ? 'bg-amber-100 text-amber-700' :
                          project.health_status === 'critical' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {project.health_status === 'excellent' ? 'Excelent' :
                           project.health_status === 'healthy' ? 'Sănătos' :
                           project.health_status === 'at_risk' ? 'La Risc' :
                           project.health_status === 'critical' ? 'Critic' : 'Fără date'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {healthData.length === 0 && (
                <div className="py-12 text-center text-muted-foreground">
                  Nu există date de Health Score pentru această perioadă
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card data-testid="project-hours-chart">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-xl font-semibold">Ore pe Proiecte</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {timeByProject.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={timeByProject}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="project_name" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={100} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="total_hours" fill="#FF5733" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-muted-foreground py-12">Nu există date</p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="project-distribution-chart">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-xl font-semibold">Distribuție Timp</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {timeByProject.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={timeByProject}
                      dataKey="total_hours"
                      nameKey="project_name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {timeByProject.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-muted-foreground py-12">Nu există date</p>
              )}
            </CardContent>
          </Card>
        </div>

        {user?.role !== 'team_member' && timeByUser.length > 0 && (
          <Card data-testid="user-hours-chart">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-xl font-semibold">Ore pe Membri Echipă</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={timeByUser}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="user_name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="total_hours" fill="#000000" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Detail Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card data-testid="project-details-table">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-xl font-semibold">Detalii Proiecte</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/20 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Proiect</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Ore</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Înreg.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeByProject.map((project, idx) => (
                      <tr key={idx} className="border-b border-border hover:bg-muted/50">
                        <td className="px-4 py-3 text-sm font-medium">{project.project_name}</td>
                        <td className="px-4 py-3 text-sm font-mono">{project.total_hours}h</td>
                        <td className="px-4 py-3 text-sm">{project.entries_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {timeByProject.length === 0 && (
                  <div className="py-12 text-center text-muted-foreground">
                    Nu există date
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {user?.role !== 'team_member' && (
            <Card data-testid="user-details-table">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-xl font-semibold">Detalii Membri Echipă</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/20 border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Nume</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Ore</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Înreg.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeByUser.map((u, idx) => (
                        <tr key={idx} className="border-b border-border hover:bg-muted/50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm font-medium">{u.user_name}</p>
                              <p className="text-xs text-muted-foreground">{u.user_email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm font-mono">{u.total_hours}h</td>
                          <td className="px-4 py-3 text-sm">{u.entries_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {timeByUser.length === 0 && (
                    <div className="py-12 text-center text-muted-foreground">
                      Nu există date
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};
